// ===========================================
// LOGBOOK — a book page, not a form
// ===========================================
// Left: the day's page — a date masthead, one quiet voice-first composer line,
// then the day as a single timeline (manager entries + the floor's end-of-shift
// reports in the same flow, category as a slim color spine, not boxes).
// Right: Patterns — what the last week/month of pages say (categories, the
// people who keep appearing, the loud days). Always visible on desktop.

import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Trash2, Loader2, Mic, MicOff, Send, Pencil, Archive, Search as SearchIcon } from 'lucide-react';
import clsx from 'clsx';
import { logbookApi } from '../services/api';

const CATEGORIES = [
  { key: 'service', label: 'Service', dot: 'bg-sky-400', spine: 'border-sky-300', chip: 'bg-sky-50 text-sky-700' },
  { key: 'food', label: 'Food', dot: 'bg-amber-400', spine: 'border-amber-300', chip: 'bg-amber-50 text-amber-700' },
  { key: 'beverage', label: 'Beverage', dot: 'bg-violet-400', spine: 'border-violet-300', chip: 'bg-violet-50 text-violet-700' },
  { key: 'team', label: 'Team', dot: 'bg-emerald-400', spine: 'border-emerald-300', chip: 'bg-emerald-50 text-emerald-700' },
  { key: 'incident', label: 'Incident', dot: 'bg-rose-500', spine: 'border-rose-400', chip: 'bg-rose-50 text-rose-700' },
  { key: 'general', label: 'General', dot: 'bg-slate-300', spine: 'border-slate-200', chip: 'bg-slate-100 text-slate-600' },
];
const cat = (k: string) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[5];
const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (day: string, n: number) => { const d = new Date(`${day}T12:00:00`); d.setDate(d.getDate() + n); return iso(d); };

const canRecord = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

export default function LogbookPage() {
  const qc = useQueryClient();
  const [day, setDay] = useState(iso(new Date()));
  const [draft, setDraft] = useState('');
  const [category, setCategory] = useState('general');
  const [listening, setListening] = useState(false);
  const [range, setRange] = useState<'week' | 'month'>('week');
  const [q, setQ] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const recRef = useRef<any>(null);
  const dateRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['logbook', day],
    queryFn: async () => (await logbookApi.day(day)).data?.data,
  });
  const { data: results } = useQuery({
    queryKey: ['logbook-search', q],
    queryFn: async () => (await logbookApi.search(q.trim())).data?.data,
    enabled: q.trim().length >= 2,
  });
  const { data: patterns } = useQuery({
    queryKey: ['logbook-analysis', range],
    queryFn: async () => (await logbookApi.analysis(addDays(iso(new Date()), range === 'week' ? -6 : -29), iso(new Date()))).data?.data,
  });
  const add = useMutation({
    mutationFn: () => logbookApi.add({ date: day, body: draft.trim(), category, spanDays }),
    onSuccess: () => { setDraft(''); setSpanDays(1); qc.invalidateQueries({ queryKey: ['logbook', day] }); qc.invalidateQueries({ queryKey: ['logbook-analysis'] }); },
  });
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => logbookApi.update(id, data),
    onSuccess: () => { setEditId(null); qc.invalidateQueries({ queryKey: ['logbook', day] }); qc.invalidateQueries({ queryKey: ['logbook-analysis'] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => logbookApi.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['logbook', day] }); qc.invalidateQueries({ queryKey: ['logbook-analysis'] }); },
  });

  // Voice notes record locally, then Whisper transcribes server-side — clean
  // punctuation, restaurant vocabulary intact, and the category picks itself.
  const [transcribing, setTranscribing] = useState(false);
  const [spanDays, setSpanDays] = useState(1);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ body: '', category: 'general', spanDays: 1 });
  const toggleMic = async () => {
    if (listening) { recRef.current?.stop(); return; }
    if (!canRecord) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 2000) return; // a tap, not a note
        setTranscribing(true);
        try {
          const res = await logbookApi.transcribe(blob);
          const d = res.data?.data;
          if (d?.text) {
            setDraft((prev) => (prev.trim() ? `${prev.trim()} ${d.text}` : d.text));
            if (d.category) setCategory(d.category);
          }
        } catch { /* the error toastless path: they can just type */ }
        finally { setTranscribing(false); }
      };
      recRef.current = rec; rec.start(); setListening(true);
    } catch { setListening(false); }
  };

  const openCalendar = () => { const el = dateRef.current; if (!el) return; try { (el as any).showPicker(); } catch { el.click(); } };

  const isToday = day === iso(new Date());
  const d = new Date(`${day}T12:00:00`);
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const monthDay = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

  const entries = data?.entries || [];
  const fromShifts = data?.fromShifts || [];

  return (
    <div className="p-4 md:p-6 pb-safe">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-6 max-w-5xl">

        {/* ——— the page ——— */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {/* masthead */}
          <div className="px-6 pt-5 pb-4 flex items-end justify-between border-b border-slate-100">
            <button onClick={openCalendar} className="text-left group" title="Pick any day">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 group-hover:text-primary-600 transition">
                {isToday ? 'Today' : weekday}
              </p>
              <p className="text-2xl font-bold text-slate-900 leading-tight">{isToday ? weekday : ''}{isToday ? ', ' : ''}{monthDay}</p>
            </button>
            <div className="flex items-center gap-0.5 pb-1">
              <button onClick={() => setDay(addDays(day, -1))} className="p-2 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
              {!isToday && <button onClick={() => setDay(iso(new Date()))} className="px-2.5 py-1 rounded-full text-xs font-semibold text-primary-600 hover:bg-primary-50">Today</button>}
              <button onClick={() => setDay(addDays(day, 1))} disabled={isToday} className="p-2 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-50 disabled:opacity-0"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <input ref={dateRef} type="date" value={day} max={iso(new Date())}
              onChange={(e) => { if (e.target.value) setDay(e.target.value); e.target.blur(); }}
              className="absolute opacity-0 pointer-events-none w-0 h-0" tabIndex={-1} aria-hidden />
          </div>

          {/* search — the whole book, not just this page */}
          <div className="px-6 py-2 border-b border-slate-100 flex items-center gap-2">
            <SearchIcon className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search the whole book…"
              className="flex-1 min-w-0 text-sm bg-transparent outline-none placeholder:text-slate-300 text-slate-700" />
            {q && <button onClick={() => setQ('')} className="text-slate-300 hover:text-slate-500 text-xs">clear</button>}
          </div>

          {/* composer — one quiet line, mic first */}
          <div className={clsx('px-6 py-3 border-b transition-colors', listening ? 'bg-rose-50/50 border-rose-100' : 'border-slate-100')}>
            <div className="flex items-center gap-3">
              {canRecord && (
                <button onClick={toggleMic} disabled={transcribing}
                  title={listening ? 'Done — tap to transcribe' : 'Dictate — talk, then tap to stop'}
                  className={clsx('shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition',
                    listening ? 'bg-rose-600 text-white animate-pulse' : transcribing ? 'bg-slate-100 text-slate-400' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100')}>
                  {listening ? <MicOff className="w-4 h-4" /> : transcribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                </button>
              )}
              <input value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim() && !add.isPending) { if (listening) recRef.current?.stop(); add.mutate(); } }}
                placeholder={listening ? 'Recording… tap the mic when you\u2019re done' : transcribing ? 'Making sense of it…' : 'Write the day down — or tap the mic and say it'}
                className="flex-1 min-w-0 text-[15px] bg-transparent outline-none placeholder:text-slate-300 text-slate-800" />
              <button onClick={() => { if (listening) recRef.current?.stop(); add.mutate(); }} disabled={!draft.trim() || add.isPending}
                className={clsx('shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition',
                  draft.trim() ? 'bg-slate-900 text-white hover:bg-slate-700' : 'text-slate-200')}>
                {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            {/* category dots + how long it rides along — only visible when writing */}
            {(draft || listening) && (
              <div className="flex items-center justify-between gap-2 mt-2 ml-12">
                <div className="flex items-center gap-1">
                  {CATEGORIES.map((c) => (
                    <button key={c.key} onClick={() => setCategory(c.key)} title={c.label}
                      className={clsx('flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] transition',
                        category === c.key ? `${c.chip} font-semibold` : 'text-slate-400 hover:bg-slate-50')}>
                      <span className={clsx('w-1.5 h-1.5 rounded-full', c.dot)} />{category === c.key ? c.label : ''}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 text-[11px] text-slate-400 shrink-0" title="A multi-day note stays on the page, aging (day 2 of 4…), until it retires">
                  <span>talk about it for</span>
                  {[1, 2, 3, 4].map((n) => (
                    <button key={n} onClick={() => setSpanDays(n)}
                      className={clsx('w-5 h-5 rounded-full font-semibold transition', spanDays === n ? 'bg-slate-800 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-500')}>{n}</button>
                  ))}
                  <span>day{spanDays > 1 ? 's' : ''}</span>
                </div>
              </div>
            )}
          </div>

          {/* while searching: the whole book's matches, tap → that day */}
          {q.trim().length >= 2 ? (
            <div className="px-6 py-4 space-y-4">
              {!results ? <p className="text-sm text-slate-300 text-center py-6">Searching…</p> : (
                (results.entries || []).length === 0 && (results.floor || []).length === 0
                  ? <p className="text-sm text-slate-300 text-center py-6 italic">Nothing in the book matches "{q.trim()}".</p>
                  : (
                    <>
                      {(results.entries || []).map((r: any) => (
                        <button key={r.id} onClick={() => { setDay(r.date); setQ(''); }} className="block w-full text-left group">
                          <div className={clsx('pl-4 border-l-2', cat(r.category).spine)}>
                            <p className="text-sm text-slate-800 leading-relaxed">{r.body}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5 group-hover:text-primary-600 transition">
                              {new Date(`${r.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {cat(r.category).label} · {r.authorName || 'Manager'}
                            </p>
                          </div>
                        </button>
                      ))}
                      {(results.floor || []).map((r: any, i: number) => (
                        <button key={`f${i}`} onClick={() => { setDay(r.date); setQ(''); }} className="block w-full text-left group">
                          <div className="pl-4 border-l-2 border-slate-100">
                            <p className="text-sm text-slate-600 leading-relaxed">{r.notes}</p>
                            <p className="text-[11px] text-slate-400 mt-0.5 group-hover:text-primary-600 transition">
                              {new Date(`${r.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {r.employee}, end of shift
                            </p>
                          </div>
                        </button>
                      ))}
                    </>
                  )
              )}
            </div>
          ) : (
          <>
          {/* who / what filters — appear only when the day has enough to filter */}
          {(() => {
            const authors = [...new Set(entries.map((e: any) => e.authorName).filter(Boolean))] as string[];
            const cats = [...new Set(entries.map((e: any) => e.category))];
            if (authors.length < 2 && cats.length < 2) return null;
            return (
              <div className="px-6 pt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                {authors.length > 1 && (
                  <>
                    <button onClick={() => setAuthorFilter('')} className={clsx('px-2 py-0.5 rounded-full', !authorFilter ? 'bg-slate-800 text-white font-semibold' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')}>Everyone</button>
                    {authors.map((a) => (
                      <button key={a} onClick={() => setAuthorFilter(authorFilter === a ? '' : a)}
                        className={clsx('px-2 py-0.5 rounded-full', authorFilter === a ? 'bg-slate-800 text-white font-semibold' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')}>{a}</button>
                    ))}
                  </>
                )}
                {cats.length > 1 && (
                  <span className="flex items-center gap-1 ml-1">
                    {CATEGORIES.filter((c) => cats.includes(c.key)).map((c) => (
                      <button key={c.key} onClick={() => setCatFilter(catFilter === c.key ? '' : c.key)} title={c.label}
                        className={clsx('w-3.5 h-3.5 rounded-full transition', c.dot, catFilter && catFilter !== c.key ? 'opacity-25' : '')} />
                    ))}
                  </span>
                )}
              </div>
            );
          })()}
          {/* the day as one timeline */}
          <div className="px-6 py-4">
            {isLoading ? <p className="text-sm text-slate-300 py-8 text-center">…</p> : (
              entries.length === 0 && fromShifts.length === 0 ? (
                <p className="text-sm text-slate-300 py-10 text-center italic">This page is blank.</p>
              ) : (
                <div className="space-y-5">
                  {entries.filter((e: any) => (!authorFilter || e.authorName === authorFilter) && (!catFilter || e.category === catFilter)).map((e: any) => (
                    <div key={e.id} className={clsx('pl-4 border-l-2 group', cat(e.category).spine)}>
                      {editId === e.id ? (
                        <div className="space-y-2">
                          <textarea rows={2} value={editForm.body} onChange={(ev) => setEditForm({ ...editForm, body: ev.target.value })}
                            className="w-full text-[15px] rounded-lg border border-slate-200 px-2.5 py-1.5 focus:ring-2 focus:ring-primary-400" />
                          <div className="flex items-center justify-between gap-2 text-[11px]">
                            <div className="flex gap-1">
                              {CATEGORIES.map((c) => (
                                <button key={c.key} onClick={() => setEditForm({ ...editForm, category: c.key })} title={c.label}
                                  className={clsx('w-4 h-4 rounded-full', c.dot, editForm.category === c.key ? 'ring-2 ring-offset-1 ring-slate-400' : 'opacity-40')} />
                              ))}
                            </div>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4].map((n) => (
                                <button key={n} onClick={() => setEditForm({ ...editForm, spanDays: n })}
                                  className={clsx('w-5 h-5 rounded-full font-semibold', editForm.spanDays === n ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500')}>{n}</button>
                              ))}
                              <span className="text-slate-400">days</span>
                            </div>
                            <div className="flex gap-1.5">
                              <button onClick={() => update.mutate({ id: e.id, data: editForm })} disabled={!editForm.body.trim()} className="btn btn-sm btn-primary">Save</button>
                              <button onClick={() => setEditId(null)} className="btn btn-sm btn-ghost">Cancel</button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-[15px] text-slate-800 leading-relaxed whitespace-pre-wrap">{e.body}</p>
                          <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
                            <span className="font-medium">{cat(e.category).label}</span>
                            {(e.spanDays || 1) > 1 && (
                              <span className="text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full font-medium">day {e.dayN} of {e.spanDays}</span>
                            )}
                            <span>·</span>
                            <span>{e.authorName || 'Manager'}, {new Date(e.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                            <span className="opacity-0 group-hover:opacity-100 transition flex items-center gap-1.5">
                              <button onClick={() => { setEditId(e.id); setEditForm({ body: e.body, category: e.category, spanDays: e.spanDays || 1 }); }}
                                className="text-slate-300 hover:text-slate-600" title="Edit"><Pencil className="w-3 h-3" /></button>
                              {(e.spanDays || 1) > (e.dayN || 1) && (
                                <button onClick={() => update.mutate({ id: e.id, data: { spanDays: e.dayN } })}
                                  className="text-slate-300 hover:text-amber-600" title="End today — stops carrying forward"><Archive className="w-3 h-3" /></button>
                              )}
                              <button onClick={() => remove.mutate(e.id)} className="text-slate-200 hover:text-rose-500" title="Delete"><Trash2 className="w-3 h-3" /></button>
                            </span>
                          </p>
                        </>
                      )}
                    </div>
                  ))}

                  {fromShifts.length > 0 && (
                    <>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-300 pt-2">From the floor</p>
                      {fromShifts.map((f: any, i: number) => (
                        <div key={i} className="pl-4 border-l-2 border-slate-100">
                          {f.notes && <p className="text-[15px] text-slate-600 leading-relaxed whitespace-pre-wrap">{f.notes}</p>}
                          {(f.issues || []).map((iss: any, j: number) => (
                            <p key={j} className="text-[15px] text-rose-700 leading-relaxed">⚑ {typeof iss === 'string' ? iss : iss?.description || ''}</p>
                          ))}
                          <p className="text-[11px] text-slate-400 mt-1">{f.employee}{f.role ? `, ${f.role}` : ''} — end of shift</p>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )
            )}
          </div>
          </>
          )}
        </div>

        {/* ——— patterns rail ——— */}
        <div className="space-y-4 lg:pt-1">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Patterns</p>
            <div className="flex rounded-full bg-slate-100 p-0.5">
              <button onClick={() => setRange('week')} className={clsx('px-2.5 py-0.5 rounded-full text-[11px] font-semibold', range === 'week' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400')}>7d</button>
              <button onClick={() => setRange('month')} className={clsx('px-2.5 py-0.5 rounded-full text-[11px] font-semibold', range === 'month' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400')}>30d</button>
            </div>
          </div>

          {!patterns ? <p className="text-xs text-slate-300">…</p> : (
            <>
              <div className="flex items-baseline gap-4 text-sm">
                <span><b className="text-slate-900 text-lg">{patterns.totals.entries}</b> <span className="text-slate-400 text-xs">entries</span></span>
                <span><b className="text-slate-900 text-lg">{patterns.totals.floorNotes}</b> <span className="text-slate-400 text-xs">floor reports</span></span>
                {patterns.totals.incidents > 0 && <span><b className="text-rose-600 text-lg">{patterns.totals.incidents}</b> <span className="text-rose-400 text-xs">incidents</span></span>}
              </div>

              {Object.keys(patterns.byCategory || {}).length > 0 && (
                <div className="space-y-1.5">
                  {CATEGORIES.filter((c) => patterns.byCategory[c.key]).map((c) => {
                    const n = patterns.byCategory[c.key];
                    const max = Math.max(...(Object.values(patterns.byCategory) as number[]));
                    return (
                      <div key={c.key} className="flex items-center gap-2 text-xs">
                        <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', c.dot)} />
                        <span className="w-16 text-slate-500">{c.label}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5"><div className={clsx('h-1.5 rounded-full', c.dot)} style={{ width: `${(n / max) * 100}%` }} /></div>
                        <span className="w-4 text-right text-slate-400 tabular-nums">{n}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {(patterns.people || []).length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-300 mb-1.5">People in the log</p>
                  <div className="space-y-1.5">
                    {(patterns.people || []).slice(0, 8).map((p: any) => (
                      <div key={p.employeeId} className="text-xs flex items-center justify-between gap-2">
                        <span className="text-slate-700 font-medium truncate">{p.name}</span>
                        <span className="text-slate-400 shrink-0">
                          {p.mentions > 0 && `${p.mentions}×`}
                          {p.issuesFlagged > 0 && <span className="text-rose-600 ml-1">⚑{p.issuesFlagged}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Object.keys(patterns.byDay || {}).length > 1 && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-300 mb-1.5">Loudest days</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(patterns.byDay).sort((a: any, b: any) => b[1] - a[1]).slice(0, 4).map(([dd, n]: any) => (
                      <button key={dd} onClick={() => setDay(dd)}
                        className="text-[11px] bg-slate-100 hover:bg-slate-200 rounded-full px-2 py-0.5 text-slate-500">
                        {new Date(`${dd}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })} · {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
